import type { BaseModelAttributes } from "./base";
import { Locale } from "./locale";
import { I18nLabels } from "./common";

export enum RoleScope {
  ADMIN = "admin",
  USER = "user",
  PROJECT = "project",
}

export enum RoleStatus {
  ACTIVE = "active",
  DISABLED = "disabled",
}

export enum ProjectMemberStatus {
  ACTIVE = "active",
  DISABLED = "disabled",
}

export enum OrgScopeType {
  ALL_CENTERS = "all_centers",
  CENTER = "center",
  DEPARTMENT = "department",
}

export enum OrgUnitType {
  CENTER = "center",
  DEPARTMENT = "department",
}

export enum DataOwnershipMode {
  PERSONAL = "personal",
  ORG_SHARED = "org_shared",
}

export enum ProjectCollaborationMode {
  PERSONAL = "personal",
  ORG_SHARED = "org_shared",
}

export enum FeatureScopeKey {
  SOURCE = "source",
  WAREHOUSE = "warehouse",
  RESEARCH_RUN = "research_run",
  RESEARCH_RESULT = "research_result",
  QUALITY_RUN = "quality_run",
  ANALYTICS = "analytics",
  EXPORT = "export",
  MANUAL_CORRECTION = "manual_correction",
}

export enum FeatureScopeMode {
  PERSONAL = "personal",
  COLLABORATION = "collaboration",
  CURRENT_FILTER_ONLY = "current_filter_only",
}

export enum DataAttributionStatus {
  ASSIGNED = "assigned",
  PENDING = "pending",
}

export enum DataAttributionSource {
  MANUAL = "manual",
  AUTO_UNIQUE_SCOPE = "auto_unique_scope",
  AUTO_SUGGESTED = "auto_suggested",
  MIGRATION_DEFAULT = "migration_default",
}

export enum DataEntryOrigin {
  MANUAL_ENTRY = "manual_entry",
  IMPORT = "import",
  DERIVED = "derived",
}

export const DataOwnershipModeLabels: I18nLabels<DataOwnershipMode> = {
  [DataOwnershipMode.PERSONAL]: { [Locale.ZH_CN]: "个人归属", [Locale.EN_US]: "Personal" },
  [DataOwnershipMode.ORG_SHARED]: { [Locale.ZH_CN]: "中心/科室共享", [Locale.EN_US]: "Organization shared" },
};

export const ProjectCollaborationModeLabels: I18nLabels<ProjectCollaborationMode> = {
  [ProjectCollaborationMode.PERSONAL]: { [Locale.ZH_CN]: "个人模式", [Locale.EN_US]: "Personal" },
  [ProjectCollaborationMode.ORG_SHARED]: { [Locale.ZH_CN]: "中心/科室协作", [Locale.EN_US]: "Organization shared" },
};

export interface IPermissionPoint extends BaseModelAttributes {
  scope: RoleScope;
  key: string;
  name: string;
  module: string;
  status: RoleStatus;
}

export interface IRole extends BaseModelAttributes {
  scope: RoleScope;
  code: string;
  name: string;
  description: string | null;
  isTemplate: boolean;
  systemBuiltin: boolean;
  status: RoleStatus;
}

export interface RoleResponse {
  id: string;
  scope: RoleScope;
  code: string;
  name: string;
  description: string | null;
  isTemplate: boolean;
  systemBuiltin: boolean;
  status: RoleStatus;
  permissionKeys?: string[];
  createdAt: Date;
  updatedAt: Date;
}

export interface MyProjectPermissionResponse {
  projectId: string;
  projectName: string;
  projectStatus: string;
  memberId: string;
  projectRoleId: string;
  projectRoleName: string | null;
  collaborationMode: ProjectCollaborationMode;
  dataOwnershipMode: DataOwnershipMode;
  permissionKeys: string[];
  organizationScope: {
    allCenters: boolean;
    centerIds: string[];
    departmentIds: string[];
  };
  canViewAllProjectData: boolean;
  canHandlePendingAttribution: boolean;
  updatedAt: Date | string;
}

export const PermissionKeys = {
  // Admin - Project Management
  AdminProjectCreate: "admin.project.create",
  AdminProjectUpdate: "admin.project.update",
  AdminProjectDelete: "admin.project.delete",
  AdminProjectRestore: "admin.project.restore",
  AdminProjectMemberView: "admin.project.member.view",
  AdminProjectMemberManage: "admin.project.member.manage",
  AdminProjectOrgView: "admin.project.org.view",
  AdminProjectOrgManage: "admin.project.org.manage",
  // Admin - Role & Permission
  AdminRoleView: "admin.role.view",
  AdminRoleManage: "admin.role.manage",
  // Admin - User Management
  AdminUserView: "admin.user.view",
  AdminUserManage: "admin.user.manage",
  // Admin - Agent Management
  AdminAgentView: "admin.agent.view",
  AdminAgentManage: "admin.agent.manage",
  // Admin - Model Gateway
  AdminModelGatewayView: "admin.model.gateway.view",
  AdminModelGatewayManage: "admin.model.gateway.manage",
  // Admin - Capability Testing
  AdminCapabilityView: "admin.capability.view",
  AdminCapabilityManage: "admin.capability.manage",
  // Admin - Queue Management
  AdminQueueView: "admin.queue.view",
  AdminQueueManage: "admin.queue.manage",
  // Admin - Audit
  AdminAuditView: "admin.audit.view",
  // Admin - Organization Management
  AdminOrgView: "admin.org.view",
  AdminOrgManage: "admin.org.manage",
  // Admin - System Settings
  AdminSystemSettingsView: "admin.system.settings.view",
  AdminSystemSettingsManage: "admin.system.settings.manage",
  // User
  UserProjectList: "user.project.list",
  UserProjectEnter: "user.project.enter",
  ExploreView: "explore.view",
  SourceView: "source.view",
  SourceImport: "source.import",
  SourceEdit: "source.edit",
  SourceCorrect: "source.correct",
  SourceExport: "source.export",
  WarehouseView: "warehouse.view",
  WarehouseReparse: "warehouse.reparse",
  WarehouseEntry: "warehouse.entry",
  WarehouseCorrect: "warehouse.correct",
  ResearchEntry: "research.entry",
  PatientView: "patient.view",
  PatientEdit: "patient.edit",
  DesignView: "design.view",
  DesignEdit: "design.edit",
  QualityPlanEdit: "qualityplan.edit",
  AnalysisPlanEdit: "analysisplan.edit",
  ResearchRun: "research.run",
  AnalysisRun: "analysis.run",
  QualityRun: "quality.run",
  ManualCorrectionApply: "manual_correction.apply",
  CorrectionApply: "correction.apply",
  CorrectionAdjudicate: "correction.adjudicate",
  ExportCreate: "export.create",
  QueryView: "query.view",
  QueryRaise: "query.raise",
  QueryRespond: "query.respond",
  QueryClose: "query.close",
  QueryAssign: "query.assign",
  DataAttributionManage: "data.attribution.manage",
  ProjectDataViewAll: "project.data.view_all",
  DataViewAll: "data.view_all",
  AuditView: "audit.view",
} as const;

export type PermissionKey = (typeof PermissionKeys)[keyof typeof PermissionKeys];

export const BuiltinPermissionPoints: Array<{
  scope: RoleScope;
  key: PermissionKey;
  name: string;
  module: string;
}> = [
  // Admin - Project Management
  { scope: RoleScope.ADMIN, key: PermissionKeys.AdminProjectCreate, name: "创建项目", module: "project" },
  { scope: RoleScope.ADMIN, key: PermissionKeys.AdminProjectUpdate, name: "更新项目", module: "project" },
  { scope: RoleScope.ADMIN, key: PermissionKeys.AdminProjectDelete, name: "删除项目", module: "project" },
  { scope: RoleScope.ADMIN, key: PermissionKeys.AdminProjectRestore, name: "恢复项目", module: "project" },
  { scope: RoleScope.ADMIN, key: PermissionKeys.AdminProjectMemberView, name: "查看项目成员", module: "project_member" },
  { scope: RoleScope.ADMIN, key: PermissionKeys.AdminProjectMemberManage, name: "管理项目成员", module: "project_member" },
  { scope: RoleScope.ADMIN, key: PermissionKeys.AdminProjectOrgView, name: "查看中心科室", module: "project_org" },
  { scope: RoleScope.ADMIN, key: PermissionKeys.AdminProjectOrgManage, name: "管理中心科室", module: "project_org" },
  // Admin - Role & Permission
  { scope: RoleScope.ADMIN, key: PermissionKeys.AdminRoleView, name: "查看角色", module: "role" },
  { scope: RoleScope.ADMIN, key: PermissionKeys.AdminRoleManage, name: "管理角色", module: "role" },
  // Admin - User Management
  { scope: RoleScope.ADMIN, key: PermissionKeys.AdminUserView, name: "查看用户", module: "user" },
  { scope: RoleScope.ADMIN, key: PermissionKeys.AdminUserManage, name: "管理用户", module: "user" },
  // Admin - Agent Management
  { scope: RoleScope.ADMIN, key: PermissionKeys.AdminAgentView, name: "查看智能体", module: "agent" },
  { scope: RoleScope.ADMIN, key: PermissionKeys.AdminAgentManage, name: "管理智能体", module: "agent" },
  // Admin - Model Gateway
  { scope: RoleScope.ADMIN, key: PermissionKeys.AdminModelGatewayView, name: "查看模型网关", module: "model_gateway" },
  { scope: RoleScope.ADMIN, key: PermissionKeys.AdminModelGatewayManage, name: "管理模型网关", module: "model_gateway" },
  // Admin - Capability Testing
  { scope: RoleScope.ADMIN, key: PermissionKeys.AdminCapabilityView, name: "查看能力测试", module: "capability" },
  { scope: RoleScope.ADMIN, key: PermissionKeys.AdminCapabilityManage, name: "管理能力测试", module: "capability" },
  // Admin - Queue Management
  { scope: RoleScope.ADMIN, key: PermissionKeys.AdminQueueView, name: "查看任务队列", module: "queue" },
  { scope: RoleScope.ADMIN, key: PermissionKeys.AdminQueueManage, name: "管理任务队列", module: "queue" },
  // Admin - Audit
  { scope: RoleScope.ADMIN, key: PermissionKeys.AdminAuditView, name: "查看平台审计日志", module: "audit" },
  // Admin - Organization Management
  { scope: RoleScope.ADMIN, key: PermissionKeys.AdminOrgView, name: "查看机构", module: "org" },
  { scope: RoleScope.ADMIN, key: PermissionKeys.AdminOrgManage, name: "管理机构", module: "org" },
  // Admin - System Settings
  { scope: RoleScope.ADMIN, key: PermissionKeys.AdminSystemSettingsView, name: "查看系统设置", module: "system_settings" },
  { scope: RoleScope.ADMIN, key: PermissionKeys.AdminSystemSettingsManage, name: "管理系统设置", module: "system_settings" },
  // User
  { scope: RoleScope.USER, key: PermissionKeys.UserProjectList, name: "查看项目列表", module: "project" },
  { scope: RoleScope.USER, key: PermissionKeys.UserProjectEnter, name: "进入项目", module: "project" },
  { scope: RoleScope.PROJECT, key: PermissionKeys.ExploreView, name: "查看数据探查", module: "explore" },
  { scope: RoleScope.PROJECT, key: PermissionKeys.SourceView, name: "查看源数据", module: "source" },
  { scope: RoleScope.PROJECT, key: PermissionKeys.SourceImport, name: "导入源数据", module: "source" },
  { scope: RoleScope.PROJECT, key: PermissionKeys.SourceEdit, name: "编辑源数据", module: "source" },
  { scope: RoleScope.PROJECT, key: PermissionKeys.SourceCorrect, name: "源数据人工修正", module: "source" },
  { scope: RoleScope.PROJECT, key: PermissionKeys.SourceExport, name: "源数据导出", module: "source" },
  { scope: RoleScope.PROJECT, key: PermissionKeys.WarehouseView, name: "查看仓库数据", module: "warehouse" },
  { scope: RoleScope.PROJECT, key: PermissionKeys.WarehouseReparse, name: "触发仓库重新解析", module: "warehouse" },
  { scope: RoleScope.PROJECT, key: PermissionKeys.WarehouseEntry, name: "仓库手动录入", module: "warehouse" },
  { scope: RoleScope.PROJECT, key: PermissionKeys.WarehouseCorrect, name: "仓库层人工修正", module: "warehouse" },
  { scope: RoleScope.PROJECT, key: PermissionKeys.ResearchEntry, name: "研究库手动录入", module: "research" },
  { scope: RoleScope.PROJECT, key: PermissionKeys.PatientView, name: "查看患者清单", module: "patient" },
  { scope: RoleScope.PROJECT, key: PermissionKeys.PatientEdit, name: "编辑患者清单", module: "patient" },
  { scope: RoleScope.PROJECT, key: PermissionKeys.DesignView, name: "查看研究方案", module: "design" },
  { scope: RoleScope.PROJECT, key: PermissionKeys.DesignEdit, name: "编辑研究方案", module: "design" },
  { scope: RoleScope.PROJECT, key: PermissionKeys.QualityPlanEdit, name: "编辑质控计划", module: "design" },
  { scope: RoleScope.PROJECT, key: PermissionKeys.AnalysisPlanEdit, name: "编辑统计方案", module: "design" },
  { scope: RoleScope.PROJECT, key: PermissionKeys.ResearchRun, name: "运行研究", module: "research" },
  { scope: RoleScope.PROJECT, key: PermissionKeys.AnalysisRun, name: "运行统计分析", module: "analysis" },
  { scope: RoleScope.PROJECT, key: PermissionKeys.QualityRun, name: "运行质控", module: "quality" },
  { scope: RoleScope.PROJECT, key: PermissionKeys.ManualCorrectionApply, name: "应用人工修订", module: "manual_correction" },
  { scope: RoleScope.PROJECT, key: PermissionKeys.CorrectionApply, name: "提交人工修正", module: "correction" },
  { scope: RoleScope.PROJECT, key: PermissionKeys.CorrectionAdjudicate, name: "裁决人工修正", module: "correction" },
  { scope: RoleScope.PROJECT, key: PermissionKeys.ExportCreate, name: "创建导出", module: "export" },
  { scope: RoleScope.PROJECT, key: PermissionKeys.QueryView, name: "查看数据质疑", module: "query" },
  { scope: RoleScope.PROJECT, key: PermissionKeys.QueryRaise, name: "发起数据质疑", module: "query" },
  { scope: RoleScope.PROJECT, key: PermissionKeys.QueryRespond, name: "响应数据质疑", module: "query" },
  { scope: RoleScope.PROJECT, key: PermissionKeys.QueryClose, name: "关闭数据质疑", module: "query" },
  { scope: RoleScope.PROJECT, key: PermissionKeys.QueryAssign, name: "指派数据质疑", module: "query" },
  { scope: RoleScope.PROJECT, key: PermissionKeys.DataAttributionManage, name: "处理待归属数据", module: "data_attribution" },
  { scope: RoleScope.PROJECT, key: PermissionKeys.ProjectDataViewAll, name: "查看全项目数据", module: "project_data" },
  { scope: RoleScope.PROJECT, key: PermissionKeys.DataViewAll, name: "查看授权范围全部数据", module: "data_scope" },
  { scope: RoleScope.PROJECT, key: PermissionKeys.AuditView, name: "查看项目审计日志", module: "audit" },
];
